import React, { memo, useState, useCallback, useMemo } from 'react';
import { ComposableMap, Geographies, Geography, Marker } from 'react-simple-maps';
import { Typography, useMediaQuery, useTheme } from '@mui/material';

const GEO_URL = '/geo/countries-110m.json';
const US_STATES_URL = '/geo/states-10m.json';

const formatCityName = (city: string) =>
  city.replace(/([A-Z])/g, ' $1').replace(/^./, s => s.toUpperCase());

// FIPS code -> state name for US states
const US_STATE_NAMES: Record<string, string> = {
  '01': 'Alabama', '02': 'Alaska', '04': 'Arizona', '05': 'Arkansas',
  '06': 'California', '08': 'Colorado', '09': 'Connecticut', '10': 'Delaware',
  '11': 'District of Columbia', '12': 'Florida', '13': 'Georgia', '15': 'Hawaii',
  '16': 'Idaho', '17': 'Illinois', '18': 'Indiana', '19': 'Iowa',
  '20': 'Kansas', '21': 'Kentucky', '22': 'Louisiana', '23': 'Maine',
  '24': 'Maryland', '25': 'Massachusetts', '26': 'Michigan', '27': 'Minnesota',
  '28': 'Mississippi', '29': 'Missouri', '30': 'Montana', '31': 'Nebraska',
  '32': 'Nevada', '33': 'New Hampshire', '34': 'New Jersey', '35': 'New Mexico',
  '36': 'New York', '37': 'North Carolina', '38': 'North Dakota', '39': 'Ohio',
  '40': 'Oklahoma', '41': 'Oregon', '42': 'Pennsylvania', '44': 'Rhode Island',
  '45': 'South Carolina', '46': 'South Dakota', '47': 'Tennessee', '48': 'Texas',
  '49': 'Utah', '50': 'Vermont', '51': 'Virginia', '53': 'Washington',
  '54': 'West Virginia', '55': 'Wisconsin', '56': 'Wyoming',
  '60': 'American Samoa', '66': 'Guam', '69': 'Northern Mariana Islands',
  '72': 'Puerto Rico', '78': 'U.S. Virgin Islands',
};

// ISO 3166-1 numeric -> display name for countries we operate in (excluding US, handled by states)
const COUNTRY_ISO_NUMERIC: Record<string, string> = {
  '380': 'Italy',
  '616': 'Poland',
  '756': 'Switzerland',
  '528': 'Netherlands',
  '250': 'France',
  '372': 'Ireland',
  '620': 'Portugal',
  '724': 'Spain',
  '826': 'United Kingdom',
};

// Map our location countryCode to ISO numeric
const COUNTRY_CODE_TO_ISO_NUMERIC: Record<string, string> = {
  IT: '380',
  PL: '616',
  CH: '756',
  NL: '528',
  FR: '250',
  IE: '372',
  PT: '620',
  ES: '724',
  GB: '826',
};

const US_ISO = '840';

// Map US city names (from locations data) to state FIPS codes
const US_CITY_TO_STATE_FIPS: Record<string, string> = {
  austin: '48',       // Texas
  chicago: '17',      // Illinois
  tulsa: '40',        // Oklahoma
  tampa: '12',        // Florida
  washington: '11',   // DC
  providence: '44',   // Rhode Island
  newYork: '36',      // New York
  houston: '48',      // Texas
  phoenix: '04',      // Arizona
  losAngeles: '06',   // California
  sanDiego: '06',     // California
  seattle: '53',      // Washington
  charlotte: '37',    // North Carolina
  raleigh: '37',      // North Carolina
  nashville: '47',    // Tennessee
  columbus: '39',     // Ohio
  denver: '08',       // Colorado
  minneapolis: '27',  // Minnesota
  lasVegas: '32',     // Nevada
};

interface Location {
  city: string;
  lat: number;
  lng: number;
  country: string;
  countryCode: string;
}

// Auto-fit: compute projection center & scale so all locations fit within the viewBox
const computeProjection = (
  locs: Location[],
  viewWidth: number,
  viewHeight: number,
  paddingPct = 0.15,
): { center: [number, number]; scale: number } => {
  if (locs.length === 0) return { center: [0, 0], scale: 200 };
  const lngs = locs.map(l => l.lng);
  const lats = locs.map(l => l.lat);
  const minLng = Math.min(...lngs);
  const maxLng = Math.max(...lngs);
  const minLat = Math.min(...lats);
  const maxLat = Math.max(...lats);

  const centerLng = (minLng + maxLng) / 2;
  const centerLat = (minLat + maxLat) / 2;

  const lngRange = (maxLng - minLng) || 20;

  // Mercator: x = scale * lng_rad, y = scale * ln(tan(pi/4 + lat_rad/2))
  const toRad = Math.PI / 180;
  const mercY = (lat: number) => Math.log(Math.tan(Math.PI / 4 + (lat * toRad) / 2));
  const yRange = Math.abs(mercY(maxLat) - mercY(minLat));
  const xRange = lngRange * toRad;

  const usableW = viewWidth * (1 - 2 * paddingPct);
  const usableH = viewHeight * (1 - 2 * paddingPct);

  const scaleX = usableW / xRange;
  const scaleY = usableH / yRange;

  return { center: [centerLng, centerLat], scale: Math.min(scaleX, scaleY) };
};

interface WorldMapProps {
  locations: Location[];
}

const pulseKeyframes = `
@keyframes pulse {
  0%, 100% { opacity: 0.6; }
  50% { opacity: 1; }
}
@keyframes pulseGlow {
  0%, 100% { opacity: 0; }
  50% { opacity: 0.25; }
}
`;

interface MapPanelProps {
  center: [number, number];
  scale: number;
  width: number;
  height: number;
  locations: Location[];
  showStates: boolean;
  highlightedCountries: Set<string>;
  highlightedStates: Set<string>;
  landFill: string;
  highlightFill: string;
  highlightHoverFill: string;
  landStroke: string;
  stateStroke: string;
  dotColor: string;
  dotHoverColor: string;
  isDark: boolean;
  hovered: { type: 'city' | 'country' | 'state'; label: string } | null;
  onCityEnter: (city: string, e: React.MouseEvent) => void;
  onCountryEnter: (geoId: string, e: React.MouseEvent) => void;
  onStateEnter: (geoId: string, e: React.MouseEvent) => void;
  onMouseMove: (e: React.MouseEvent) => void;
  onMouseLeave: () => void;
  dotIndexOffset?: number;
}

const MapPanel: React.FC<MapPanelProps> = ({
  center, scale, width, height, locations, showStates,
  highlightedCountries, highlightedStates,
  landFill, highlightFill, highlightHoverFill, landStroke, stateStroke,
  dotColor, dotHoverColor, isDark,
  hovered, onCityEnter, onCountryEnter, onStateEnter, onMouseMove, onMouseLeave,
  dotIndexOffset = 0,
}) => (
  <ComposableMap
    projection="geoMercator"
    projectionConfig={{ center, scale }}
    width={width}
    height={height}
    style={{ width: '100%', height: '100%' }}
  >
    <Geographies geography={GEO_URL}>
      {({ geographies }) =>
        geographies.map(geo => {
          if (showStates && geo.id === US_ISO) return null;
          const isHighlighted = highlightedCountries.has(geo.id);
          return (
            <Geography
              key={geo.rpisvn}
              geography={geo}
              fill={isHighlighted ? highlightFill : landFill}
              stroke={landStroke}
              strokeWidth={0.5}
              style={{
                default: { outline: 'none', cursor: isHighlighted ? 'pointer' : 'default' },
                hover: {
                  outline: 'none',
                  fill: isHighlighted ? highlightHoverFill : landFill,
                  cursor: isHighlighted ? 'pointer' : 'default',
                },
                pressed: { outline: 'none' },
              }}
              onMouseEnter={(e: any) => onCountryEnter(geo.id, e)}
              onMouseMove={onMouseMove}
              onMouseLeave={onMouseLeave}
            />
          );
        })
      }
    </Geographies>

    {showStates && (
      <Geographies geography={US_STATES_URL}>
        {({ geographies }) =>
          geographies.map(geo => {
            const isHighlighted = highlightedStates.has(geo.id);
            return (
              <Geography
                key={geo.rpisvn}
                geography={geo}
                fill={isHighlighted ? highlightFill : landFill}
                stroke={stateStroke}
                strokeWidth={0.3}
                style={{
                  default: { outline: 'none', cursor: isHighlighted ? 'pointer' : 'default' },
                  hover: {
                    outline: 'none',
                    fill: isHighlighted ? highlightHoverFill : landFill,
                    cursor: isHighlighted ? 'pointer' : 'default',
                  },
                  pressed: { outline: 'none' },
                }}
                onMouseEnter={(e: any) => isHighlighted && onStateEnter(geo.id, e)}
                onMouseMove={isHighlighted ? onMouseMove : undefined}
                onMouseLeave={isHighlighted ? onMouseLeave : undefined}
              />
            );
          })
        }
      </Geographies>
    )}

    {locations.map((loc, i) => {
      const globalIndex = i + dotIndexOffset;
      const isHovered = hovered?.type === 'city' && hovered.label === formatCityName(loc.city);
      const isOffice = loc.city === 'warsaw' || loc.city === 'zurich';
      return (
        <Marker key={i} coordinates={[loc.lng, loc.lat]}>
          <circle
            r={isHovered ? 12 : isOffice ? 10 : 8}
            fill={dotColor}
            opacity={isHovered ? 0.2 : 0}
            style={{
              animation: isHovered ? 'none' : 'pulseGlow 2.5s ease-in-out infinite',
              animationDelay: `${(globalIndex * 0.3) % 2.5}s`,
            }}
          />
          <circle
            r={isHovered ? 5 : isOffice ? 4 : 3}
            fill={isHovered ? dotHoverColor : dotColor}
            stroke={isDark ? 'rgba(255,255,255,0.8)' : '#fff'}
            strokeWidth={isHovered ? 1.5 : 1}
            style={{
              cursor: 'pointer',
              animation: isHovered ? 'none' : 'pulse 2.5s ease-in-out infinite',
              animationDelay: `${(globalIndex * 0.3) % 2.5}s`,
            }}
            onMouseEnter={(e) => onCityEnter(loc.city, e)}
            onMouseMove={onMouseMove}
            onMouseLeave={onMouseLeave}
          />
        </Marker>
      );
    })}
  </ComposableMap>
);

export const WorldMap: React.FC<WorldMapProps> = memo(({ locations }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [hovered, setHovered] = useState<{ type: 'city' | 'country' | 'state'; label: string } | null>(null);
  const [tooltipPos, setTooltipPos] = useState<{ x: number; y: number }>({ x: 0, y: 0 });

  const highlightedCountries = useMemo(() => {
    const codes = new Set<string>();
    locations.forEach(l => {
      const numeric = COUNTRY_CODE_TO_ISO_NUMERIC[l.countryCode];
      if (numeric) codes.add(numeric);
    });
    return codes;
  }, [locations]);

  const highlightedStates = useMemo(() => {
    const fips = new Set<string>();
    locations.forEach(l => {
      const code = US_CITY_TO_STATE_FIPS[l.city];
      if (code) fips.add(code);
    });
    return fips;
  }, [locations]);

  const { usLocations, europeLocations } = useMemo(() => ({
    usLocations: locations.filter(l => l.countryCode === 'US'),
    europeLocations: locations.filter(l => l.countryCode !== 'US'),
  }), [locations]);

  const updateTooltipPos = useCallback((e: React.MouseEvent) => {
    const container = (e.currentTarget as Element).closest('[data-worldmap]');
    if (container) {
      const rect = container.getBoundingClientRect();
      setTooltipPos({
        x: e.clientX - rect.left,
        y: e.clientY - rect.top,
      });
    }
  }, []);

  const handleCityEnter = useCallback((city: string, e: React.MouseEvent) => {
    setHovered({ type: 'city', label: formatCityName(city) });
    updateTooltipPos(e);
  }, [updateTooltipPos]);

  const handleCountryEnter = useCallback((geoId: string, e: React.MouseEvent) => {
    const name = COUNTRY_ISO_NUMERIC[geoId];
    if (name) {
      setHovered({ type: 'country', label: name });
      updateTooltipPos(e);
    }
  }, [updateTooltipPos]);

  const handleStateEnter = useCallback((geoId: string, e: React.MouseEvent) => {
    const name = US_STATE_NAMES[geoId];
    if (name) {
      setHovered({ type: 'state', label: name });
      updateTooltipPos(e);
    }
  }, [updateTooltipPos]);

  const handleMouseLeave = useCallback(() => setHovered(null), []);

  const landFill = isDark ? '#2d2d2f' : '#dddde5';
  const highlightFill = isDark ? '#353560' : '#c8c8e8';
  const highlightHoverFill = isDark ? '#3d3d70' : '#b8b8e0';
  const landStroke = isDark ? '#3d3d40' : '#c0c0cc';
  const stateStroke = isDark ? '#3d3d50' : '#b0b0c0';
  const bgColor = isDark ? '#1a1a1e' : '#ededf3';
  const dotColor = theme.palette.primary.main;
  const dotHoverColor = theme.palette.secondary.main;

  const sharedProps = {
    highlightedCountries,
    highlightedStates,
    landFill,
    highlightFill,
    highlightHoverFill,
    landStroke,
    stateStroke,
    dotColor,
    dotHoverColor,
    isDark,
    hovered,
    onCityEnter: handleCityEnter,
    onCountryEnter: handleCountryEnter,
    onStateEnter: handleStateEnter,
    onMouseMove: updateTooltipPos,
    onMouseLeave: handleMouseLeave,
  };

  const tooltipEl = hovered && (
    <div
      style={{
        position: 'absolute',
        left: tooltipPos.x,
        top: tooltipPos.y - 36,
        transform: 'translateX(-50%)',
        background: isDark ? 'rgba(99,101,255,0.85)' : theme.palette.primary.main,
        color: '#fff',
        padding: '5px 12px',
        borderRadius: 4,
        fontSize: 12,
        fontFamily: theme.typography.fontFamily as string,
        fontWeight: 600,
        pointerEvents: 'none' as const,
        whiteSpace: 'nowrap' as const,
        boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
        zIndex: 10,
        letterSpacing: '0.02em',
      }}
    >
      {hovered.label}
      <div
        style={{
          position: 'absolute',
          bottom: -4,
          left: '50%',
          transform: 'translateX(-50%) rotate(45deg)',
          width: 8,
          height: 8,
          background: isDark ? 'rgba(99,101,255,0.85)' : theme.palette.primary.main,
        }}
      />
    </div>
  );

  const mobileViewW = 600;
  const mobileViewH = 450;
  const usProjection = useMemo(
    () => computeProjection(usLocations, mobileViewW, mobileViewH, 0.12),
    [usLocations],
  );
  const europeProjection = useMemo(
    () => computeProjection(europeLocations, mobileViewW, mobileViewH, 0.12),
    [europeLocations],
  );

  if (isMobile) {
    const panelStyle: React.CSSProperties = {
      position: 'relative',
      overflow: 'hidden',
      margin: '0 16px',
      borderRadius: 12,
      background: isDark ? '#222225' : '#e4e4ec',
      boxShadow: isDark
        ? '0 2px 12px rgba(0,0,0,0.4)'
        : '0 2px 12px rgba(0,0,0,0.08)',
    };

    const labelSx = {
      position: 'absolute' as const,
      top: 10,
      left: '50%',
      transform: 'translateX(-50%)',
      zIndex: 5,
      letterSpacing: '0.15em',
      fontSize: 10,
      opacity: 0.7,
    };

    return (
      <div style={{ width: '100%', background: bgColor, padding: '16px 0', display: 'flex', flexDirection: 'column' as const, gap: 16 }}>
        <style>{pulseKeyframes}</style>

        {/* North America */}
        <div data-worldmap="" style={panelStyle}>
          <Typography variant="overline" color="textSecondary" sx={labelSx}>
            North America
          </Typography>
          <MapPanel
            center={usProjection.center}
            scale={usProjection.scale}
            width={mobileViewW}
            height={mobileViewH}
            locations={usLocations}
            showStates
            dotIndexOffset={0}
            {...sharedProps}
          />
          {tooltipEl}
        </div>

        {/* Europe */}
        <div data-worldmap="" style={panelStyle}>
          <Typography variant="overline" color="textSecondary" sx={labelSx}>
            Europe
          </Typography>
          <MapPanel
            center={europeProjection.center}
            scale={europeProjection.scale}
            width={mobileViewW}
            height={mobileViewH}
            locations={europeLocations}
            showStates={false}
            dotIndexOffset={usLocations.length}
            {...sharedProps}
          />
          {tooltipEl}
        </div>
      </div>
    );
  }

  return (
    <div
      data-worldmap=""
      style={{
        width: '100%',
        height: '100vh',
        background: bgColor,
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      <style>{pulseKeyframes}</style>
      <MapPanel
        center={[-46, 44]}
        scale={370}
        width={960}
        height={600}
        locations={locations}
        showStates
        {...sharedProps}
      />
      {tooltipEl}
    </div>
  );
});
